<template>
	<div class="modal-container modal-xx-large modal-document-preview">
		<div class="modal-header">
			<span class="header-title">{{ title }}</span>

			<div class="header-actions">
				<button
					class="button-info"
					@click="toggleMetadataScreen"
				>
					<i :class="['fas', showMetadataScreen ? 'fa-file' : 'fa-info-circle']" />
					<span>{{ showMetadataScreen ? 'View Document' : 'View Metadata' }}</span>
				</button>

				<ButtonLoading
					:isLoading="isDownloading"
					class="button-accept"
					@click="downloadDocument"
				>
					<i class="fas fa-download" /> Download
				</ButtonLoading>
			</div>
		</div>

		<div class="modal-body">
			<div
				v-show="!showMetadataScreen"
				:class="{ 'document-main-preview': true, 'show-fields-to-match': !shouldHideFieldsToMatch }"
			>
				<DocumentPreview
					:document="document"
					hideCaption
				/>

				<div
					v-if="!shouldHideFieldsToMatch"
					class="fields-to-match-container"
				>
					<div class="selected-manual-verification">
						<InputRichDropdown
							label="Manual Verification"
							v-model:saturn="currentFieldsToMatchId"
							:list="fieldsToMatchLists"
							:display="(d: IDocumentFieldsToMatchHistory) => d.display"
							:valueMap="(d: IDocumentFieldsToMatchHistory) => d.id"
						/>
					</div>

					<template v-if="currentFieldsToMatch">
						<div
							v-for="field in currentFieldsToMatch.fieldsToMatch"
							:key="field.key"
							class="field-to-match"
						>
							<b>{{ field.key }}</b>
							<span>{{ field.value }}</span>
						</div>
					</template>
					<span
						v-else
						class="no-verification-selected-msg"
					>
						Please select a manual verification to view fields matched against this document.
					</span>
				</div>
			</div>

			<div
				v-show="showMetadataScreen"
				class="document-metadata"
			>
				<h3 style="margin-top: 0">General Information</h3>
				<table class="borderless-table">
					<tbody>
						<tr>
							<th>File Name</th>
							<td>{{ document.docInfo.fileName }}</td>
						</tr>
						<tr>
							<th>File Type</th>
							<td>{{ document.docInfo.fileType }}</td>
						</tr>
						<tr>
							<th>Document Type</th>
							<td>{{ docType }}</td>
						</tr>
						<tr>
							<th>Source</th>
							<td>{{ document.docInfo.source }}</td>
						</tr>
						<tr>
							<th>Date Uploaded</th>
							<td>{{ timestampFormatted }}</td>
						</tr>
					</tbody>
				</table>

				<h3>Document History</h3>
				<RichTable
					:headers="TABLE_HEADERS"
					:tableData="combinedDocHistory"
					noData="No history is available for this document."
					noUppercase
					:showResetFiltersButton="false"
				/>
			</div>
		</div>

		<div class="modal-footer">
			<button
				class="button-unwind"
				@click="cancel()"
			>
				Close
			</button>
		</div>
	</div>
</template>
<script lang="ts">
	export interface IModalDocumentPreviewProps {
		modal: {
			passedData: {
				title?: string;
				document: IUploadedDocument;
				hideFieldsToMatch?: boolean;
			};
		};
	}
</script>
<script setup lang="ts">
	import { computed, defineAsyncComponent, onBeforeMount, ref } from 'vue';

	import $modal from '@core/services/modal';
	import api from '@core/services/api/_api';
	import settings from 'settings';
	import util from '@core/services/util';

	import { DocAssociation } from '@core/classes/SharedEnums';
	import IDocumentType from '@core/_interfaces/DocumentType';
	import { IUploadedDocument } from '@core/classes/UploadedDocument';

	import ButtonLoading from '@core/components/ButtonLoading.vue';
	import InputRichDropdown from '@core/components/InputRichDropdown.vue';
	import RichTable from '@core/components/RichTable.vue';
	const DocumentPreview = defineAsyncComponent(() => import('@core/components/DocumentPreview.vue'));

	// #region Interfaces & Constants
	interface IDocumentHistory {
		timestamp: Date;
		statusText: string;
		updatedBy: string;
		updaterType: DocAssociation;
	}

	interface IDocumentFieldsToMatchHistory {
		id: string;
		timestamp: Date;
		verifiedBy: string;
		display: string;
		fieldsToMatch: { key: string; value: string }[];
	}

	const TABLE_HEADERS = [
		{
			name: 'statusText',
			display: 'Event',
			autoFilter: true,
		},
		{
			name: 'updatedBy',
			display: 'Updated By',
			autoFilter: true,
		},
		{
			name: 'updaterType',
			display: 'Updater Type',
			filter: (value: DocAssociation) => util.formatEnumKey(DocAssociation[value], true),
			autoFilter: true,
		},
		{
			name: 'timestamp',
			display: 'Timestamp',
			filter: (value: Date) => util.formatDate(value, 'short', 'long'),
			sortable: true,
		},
	];
	// #endregion

	const props = defineProps<IModalDocumentPreviewProps>();

	const title = computed((): string => props.modal.passedData.title ?? 'Document Preview');
	const document = computed((): IUploadedDocument => props.modal.passedData.document);

	// #region Document Metadata
	const docType = computed((): string => {
		if (!document.value) return null;

		const foundDoc = settings?.lookups?.uploadedDocumentTypes?.find((d: IDocumentType) => d.code === document.value.docInfo.docType) as IDocumentType;
		return foundDoc?.display ?? document.value.docInfo.docType;
	});

	const timestampFormatted = computed((): string => {
		return util.formatDate(document.value.docInfo.timestamp, 'long', 'medium');
	});

	const combinedDocHistory = computed((): IDocumentHistory[] => {
		if (!document.value) return [];

		const history: IDocumentHistory[] = [];

		document.value.auditTrail.forEach(a => {
			history.push({
				timestamp: new Date(a.timestamp),
				statusText: util.formatEnumKey(a.status, true),
				updatedBy: a.statusUpdatedBy,
				updaterType: a.updaterType,
			});
		});

		document.value.docInfo.manualVerifications.forEach(m => {
			// Employee names are saved as Last Name, First Name
			let employeeName = m.verifiedBy.employeeName;
			if (employeeName.indexOf(',') !== -1) {
				const employeeSplit = employeeName.split(', ');
				employeeName = employeeSplit.length < 2 ? employeeSplit[0] : `${employeeSplit[1]} ${employeeSplit[0]}`;
			}

			history.push({
				timestamp: new Date(Date.parse(m.timestamp)),
				statusText: m.isVerified ? 'Manually Verified' : 'Manually Rejected',
				updatedBy: employeeName,
				updaterType: DocAssociation.Employee,
			});
		});

		history.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
		return history;
	});
	// #endregion

	// #region Fields-To-Match
	const fieldsToMatchLists = computed((): IDocumentFieldsToMatchHistory[] => {
		const history: IDocumentFieldsToMatchHistory[] = [];
		document.value.docInfo.manualVerifications.forEach(m => {
			if (!m.fieldsMatchedAgainst || m.fieldsMatchedAgainst.length < 1) return;

			const statusText = m.isVerified ? 'Verified' : 'Rejected';
			const dateFormatted = util.formatDate(m.timestamp, 'short', 'short');

			history.push({
				id: crypto.randomUUID(),
				timestamp: new Date(Date.parse(m.timestamp)),
				verifiedBy: m.verifiedBy.employeeName,
				display: `${statusText} by ${m.verifiedBy.employeeName} (${dateFormatted})`,
				fieldsToMatch: m.fieldsMatchedAgainst,
			});
		});

		history.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
		return history;
	});

	const currentFieldsToMatchId = ref<string>(null);

	const currentFieldsToMatch = computed((): IDocumentFieldsToMatchHistory => {
		if (!currentFieldsToMatchId.value) return null;
		return fieldsToMatchLists.value.find(f => f.id === currentFieldsToMatchId.value);
	});

	const shouldHideFieldsToMatch = computed((): boolean => {
		return props.modal.passedData.hideFieldsToMatch || fieldsToMatchLists.value.length < 1;
	});
	// #endregion

	const showMetadataScreen = ref(false);
	const isDownloading = ref(false);

	onBeforeMount(() => {
		if (fieldsToMatchLists.value && fieldsToMatchLists.value.length > 0) {
			const lastIndex = fieldsToMatchLists.value.length - 1;
			currentFieldsToMatchId.value = fieldsToMatchLists.value[lastIndex].id;
		}
	});

	const cancel = () => {
		$modal.cancel();
	};

	const toggleMetadataScreen = () => {
		showMetadataScreen.value = !showMetadataScreen.value;
	};

	const downloadDocument = async () => {
		isDownloading.value = true;

		try {
			await api.documents.downloadDocument(document.value.id, document.value.docInfo.fileName);
		} catch (err) {
			console.error('Error downloading document: ', err);
			util.toastr('error', 'Error Downloading File', 'An error occurred when trying to download the file from the server.');
		} finally {
			isDownloading.value = false;
		}
	};
</script>
<style>
	.modal-document-preview .modal-header .header-actions {
		display: flex;
		gap: 10px;
		align-items: center;
	}

	.modal-document-preview .modal-header .header-title {
		width: fit-content;
		max-width: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.modal-document-preview .modal-header .header-actions button {
		width: fit-content;
		display: flex;
		gap: 1ch;
		align-items: center;
	}

	.modal-document-preview .modal-body .document-main-preview.show-fields-to-match {
		width: 100%;
		height: 640px;
		display: grid;
		grid-template-columns: 1fr 450px;
		gap: 10px;
		overflow: hidden;
	}

	.modal-document-preview .modal-body .document-main-preview.show-fields-to-match .no-verification-selected-msg {
		margin: 5px;
		display: block;
		font-weight: bold;
		font-size: 1.2rem;
		color: var(--main-color);
	}

	.modal-document-preview .modal-body .document-main-preview.show-fields-to-match .document-preview-container:not(.is-loading) {
		overflow-y: auto;
		align-items: flex-start;
	}

	.modal-document-preview .modal-body .document-main-preview:not(.show-fields-to-match) .document-preview-container {
		display: flex;
		justify-content: center;
	}

	.modal-document-preview .modal-body .document-main-preview.show-fields-to-match .fields-to-match-container {
		padding: 0 10px 10px 10px;
		overflow-y: auto;
	}

	.modal-document-preview .modal-body .document-main-preview.show-fields-to-match .fields-to-match-container .selected-manual-verification {
		position: sticky;
		top: 0;
		padding: 10px 0;
		background-color: var(--background-color);
	}

	.modal-document-preview .modal-body .document-main-preview.show-fields-to-match .fields-to-match-container .field-to-match {
		display: flex;
		flex-direction: column;
		margin: 10px 0;
		padding: 10px;
		background-color: var(--card-background-color);
		border: 1px solid var(--border-color);
		border-radius: var(--default-border-radius);
	}

	.modal-document-preview .modal-body .document-main-preview.show-fields-to-match .fields-to-match-container .field-to-match > span {
		white-space: pre-wrap;
	}

	.modal-document-preview .modal-body .document-metadata b {
		display: block;
	}

	.modal-document-preview .modal-body .document-metadata table.borderless-table {
		border-top: 1px solid var(--border-color);
		border-bottom: 1px solid var(--border-color);
	}
</style>
